import React, { useEffect, useRef, useState } from 'react';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { DataProviderInstance } from '../../api/DataProvider';
import RoleGuard from '../../components/guards/RoleGuard';
import AccessDenied from '../../components/access-denied/AccessDenied';
import CustomButton from '../../components/buttons/CustomButton';
import HiddenReport from '../../components/documents-templates/report/HiddenReport';
import useReportExport from '../../components/documents-templates/report/useReportExport';
import { SUBSCRIPTION_PERMISSIONS } from '../../consts/SubscriptionFeatures';
import usePlanLock from '../../hooks/usePlanLock';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useGuidedTour } from '../../components/guided-tour/GuidedTourContext';
import ModuleCard from '../appointment/components/moduleCard/ModuleCard';
import DemoDataNote from '../../components/demo-data/DemoDataNote';
import ModuleNav from '../appointment/components/moduleNav/ModuleNav';
import useStickyHeight from './components/useStickyHeight';
import PeriodPicker from './components/PeriodPicker';
import { StatsCompareContext } from './components/compareContext';
import SectionState from './components/SectionState';
import StatisticsReport from './report/StatisticsReport';
import StatisticsLockedPreview from './components/StatisticsLockedPreview';
import STATS_SECTION_LIST from './sections';
import useClinicStats from './useClinicStats';
import { DEFAULT_PRESET, compareLabels, describePeriod, guidePeriod, presetPeriod } from './utils/periods';
import { monthlyGoal } from './utils/goal';
import styles from './Statistics.module.css';

/**
 * Estatísticas da clínica: um card por área, todos abertos, com o menu
 * de atalhos da tela de atendimento. Cada área carrega sozinha
 * (statistics/get.php) e, se a pessoa ligar a comparação no cabeçalho,
 * compara com o período anterior de mesmo tamanho ou com o mesmo período do
 * ano passado.
 */
// Mesmo ponto do menu de seções (ModuleNav), somado à barra de períodos fixa
const NAV_ACTIVE_OFFSET = 48;

// Confere o papel antes de montar a tela: sem acesso, nenhuma seção chega a pedir dados
function Statistics() {
  return (
    <RoleGuard
      access="statistics"
      fallback={(
        <AccessDenied
          title="As estatísticas ficam com quem administra a conta"
          description="Atendimentos, faturamento e desempenho do período aparecem para Administrador(a) e Proprietário(a)."
          hint="Peça a quem é proprietário(a) da conta para mudar o seu papel em Usuários."
          preview={<StatisticsLockedPreview />}
        />
      )}
    >
      <StatisticsContent />
    </RoleGuard>
  );
}

function StatisticsContent() {
  const { CompanyConfig } = DataProviderInstance();
  // No passo das Estatísticas do guia, abre num período que inclui o atendimento recém-marcado
  const tour = useGuidedTour();
  const inTour = tour.activeKey === 'statistics' && !tour.finishedKey;
  const [period, setPeriod] = useState(() => (inTour ? guidePeriod() : presetPeriod(DEFAULT_PRESET)));
  const tourPeriodApplied = useRef(inTour);
  useEffect(() => {
    // Ao recarregar a página, o guia volta depois da primeira renderização
    if (!inTour || tourPeriodApplied.current) return;
    tourPeriodApplied.current = true;
    setPeriod(guidePeriod());
  }, [inTour]);
  const periodBar = useStickyHeight();

  // Cadeados por plano: a área continua no menu e na página, com o banner
  const locks = {
    leads: usePlanLock(SUBSCRIPTION_PERMISSIONS.LEADS_FUNNEL, 'O funil de leads'),
    satisfaction: usePlanLock(
      [SUBSCRIPTION_PERMISSIONS.PLATFORM_ENGAGEMENT, SUBSCRIPTION_PERMISSIONS.ZAPI_INTEGRATION],
      'A pesquisa de satisfação e o convite para avaliar no Google',
      undefined,
      { plural: true }
    ),
  };
  const compare = compareLabels(period);
  const goal = monthlyGoal(CompanyConfig);
  const lockedSections = Object.keys(locks).filter((key) => locks[key].locked);
  // Só pede as seções depois de ler o plano: antes disso o cadeado ainda não sabe o que bloquear
  const { loading: planLoading, planConfig } = useSubscription();
  const planReady = !planLoading || planConfig !== null;
  const { sections, reload } = useClinicStats(period, lockedSections, planReady);
  const loading = Object.values(sections).some((state) => state.status === 'loading');

  const report = useReportExport({
    patientName: 'Estatisticas',
    companyConfig: CompanyConfig,
    reportType: `${period.start}_a_${period.end}`,
    prepare: () => ({ period, sections }),
  });

  const navSections = STATS_SECTION_LIST.map(({ id, label, icon, accent }) => ({ id, label, icon, accent }));

  return (
    <div className='page-content' style={{ gap: '16px', display: 'flex', flexDirection: 'column' }}>
      <ModuleCard
        title="Estatísticas"
        icon={<BarChartOutlinedIcon />}
        description={`${describePeriod(period.start, period.end)}${compare.enabled ? `, ${compare.long}` : ''}`}
        actions={(
          <CustomButton
            variant="outlined"
            style="secondary"
            label={report.exporting ? 'Gerando...' : 'Baixar PDF'}
            icon={<PictureAsPdfOutlinedIcon />}
            enabled={!loading && !report.exporting}
            onClick={report.start}
          />
        )}
      />

      <DemoDataNote />

      <StatsCompareContext.Provider value={compare}>
        <div className={styles.layout} style={{ '--stats-bar': `${periodBar.height}px` }}>
          <ModuleNav
            sections={navSections}
            title="Seções"
            ariaLabel="Seções das estatísticas"
            activeOffset={NAV_ACTIVE_OFFSET + periodBar.height}
          />
          <div className={styles.sections}>
            {/* Períodos acompanham a rolagem: a tela é longa e trocar o período é o uso mais comum */}
            <div ref={periodBar.ref} className={styles.periodBar}>
              <PeriodPicker value={period} onChange={setPeriod} />
            </div>
            {STATS_SECTION_LIST.map(({ id, label, description, icon, accent, Component }) => (
              <div key={id} id={id} className={styles.anchor} data-tour={id === 'overview' ? 'statistics-overview' : undefined}>
                <ModuleCard title={label} description={description} icon={icon} accent={accent}>
                  <SectionState state={sections[id]} lock={locks[id]} onRetry={() => reload(id)}>
                    {(data, granularity) => <Component data={data} granularity={granularity} period={period} goal={goal} />}
                  </SectionState>
                </ModuleCard>
              </div>
            ))}
          </div>
        </div>
      </StatsCompareContext.Provider>

      {locks.leads.modal}
      {locks.satisfaction.modal}
      {report.job && (
        <HiddenReport>
          <StatisticsReport ref={report.ref} companyData={CompanyConfig} {...report.job} />
        </HiddenReport>
      )}
    </div>
  );
}

export default Statistics;
